// src/features/dashboard/admin/tutor/TutorContent.jsx
import React, { useState } from 'react';
import { useAuth } from '../../../../hooks/useAuth';
import { SUBJECTS } from '../../../../data/subjects';
import { GRADE_LEVELS } from '../../../../data/gradeLevels';
import { CLASS_STREAMS } from '../../../../data/classStreams';
import AttendanceRegister from '../../teacher/AttendanceRegister';
import MarksEntry from '../../teacher/MarksEntry';
import TopicsManager from '../../teacher/TopicsManager';
import EditStudentScheduleModal from '../../teacher/EditStudentScheduleModal';
import EditTutoringStudentModal from './EditTutoringStudentModal';
import TutorImportModal from './TutorImportModal';
import { updateStudentSchedule, addTutorSubject } from '../../../../services/sheetDataService';



const TutorContent = ({ selectedStudent, students, onStudentUpdated, onEditStudent }) => {
  const { user, accessToken } = useAuth();
  const [activeSubject, setActiveSubject] = useState(null);
  const [activeTab, setActiveTab] = useState('attendance');
  const [showEditModal, setShowEditModal] = useState(false);
  const [editSubject, setEditSubject] = useState(null);
  const [showEditStudentModal, setShowEditStudentModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showAddSubject, setShowAddSubject] = useState(false);
  const [newSubject, setNewSubject] = useState({
    subject: '',
    gradeLevel: '',
    stream: '',
    googleClassroomLink: '',
    zoomLink: '',
    zoomPassword: '',
    schedule: [{ day: '', startTime: '', duration: '' }],
    termData: [{ term: '', termStart: '', termEnd: '' }],
  });
  const [addingSubject, setAddingSubject] = useState(false);

  // Derive spreadsheet IDs from user object
  const usersSheetId = user.usersSpreadsheetId || user.spreadsheetId;
  const attendanceSheetId = user.attendanceSpreadsheetId || user.spreadsheetId;
  const marksSheetId = user.marksSpreadsheetId || user.spreadsheetId;
  const topicsSheetId = user.topicsSpreadsheetId || user.spreadsheetId;

  if (!selectedStudent) {
    return (
      <div className="bg-white rounded shadow p-4 text-center text-gray-500">
        <p>Select a student to view their details.</p>
      </div>
    );
  }

  const subjects = selectedStudent.subjects || [selectedStudent];
  const currentSubject = activeSubject || subjects[0];

  const handleEditSchedule = (subject) => {
    setEditSubject(subject);
    setShowEditModal(true);
  };

  /*const handleSaveSchedule = async (userId, schedule) => {
    try {
      await updateStudentSchedule(accessToken, usersSheetId, userId, schedule);
      onStudentUpdated();
      alert('✅ Schedule updated successfully!');
    } catch (err) {
      alert('❌ Failed to update schedule: ' + err.message);
    }
    setShowEditModal(false);
  };*/
  /*const handleSaveSchedule = async (userId, schedule, termData) => {
    try {
      // ✅ Pass both schedule and termData
      await updateStudentSchedule(accessToken, usersSheetId, userId, schedule, termData);
      onStudentUpdated();
      alert('✅ Schedule updated successfully!');
    } catch (err) {
      alert('❌ Failed to update schedule: ' + err.message);
    }
    setShowEditModal(false);
  };*/

  const handleSaveSchedule = async (rowNumber, schedule, termData) => {
    try {
      await updateStudentSchedule(accessToken, usersSheetId, rowNumber, schedule, termData);
      onStudentUpdated();
      alert('✅ Schedule updated successfully!');
    } catch (err) {
      alert('❌ Failed to update schedule: ' + err.message);
    }
    setShowEditModal(false);
  };

  const handleAddSubject = async (e) => {
    e.preventDefault();
    const validSessions = newSubject.schedule.filter(s => s.day && s.startTime && s.duration);
    if (!newSubject.subject || !newSubject.gradeLevel) {
      alert('Please fill in Subject and Grade Level.');
      return;
    }
    if (validSessions.length === 0) {
      alert('Please add at least one valid session.');
      return;
    }

    // ✅ Filter out empty term rows
    const validTerms = newSubject.termData.filter(t => t.term && t.termStart && t.termEnd);

    const combinedGrade = `${newSubject.gradeLevel}${newSubject.stream}`.trim();
    const subjectData = {
      ...newSubject,
      grade: combinedGrade,
      termData: validTerms, // 👈 Include termData
    };
    delete subjectData.gradeLevel;
    delete subjectData.stream;

    setAddingSubject(true);
    try {
      await addTutorSubject(accessToken, usersSheetId, selectedStudent.userId, subjectData);
      onStudentUpdated();
      setShowAddSubject(false);
      setNewSubject({
        subject: '',
        gradeLevel: '',
        stream: '',
        googleClassroomLink: '',
        zoomLink: '',
        zoomPassword: '',
        schedule: [{ day: '', startTime: '', duration: '' }],
        termData: [{ term: '', termStart: '', termEnd: '' }],
      });
      alert('✅ Subject added successfully!');
    } catch (err) {
      alert('❌ Failed to add subject: ' + err.message);
    } finally {
      setAddingSubject(false);
    }
  };

  const handleNewSubjectChange = (field, value) => {
    setNewSubject(prev => ({ ...prev, [field]: value }));
  };

  const addSession = () => {
    setNewSubject(prev => ({
      ...prev,
      schedule: [...prev.schedule, { day: '', startTime: '', duration: '' }]
    }));
  };

  const removeSession = (index) => {
    if (newSubject.schedule.length <= 1) {
      alert('You need at least one session.');
      return;
    }
    const newSchedule = newSubject.schedule.filter((_, i) => i !== index);
    setNewSubject(prev => ({ ...prev, schedule: newSchedule }));
  };

  const updateSession = (index, field, value) => {
    const newSchedule = [...newSubject.schedule];
    newSchedule[index][field] = value;
    setNewSubject(prev => ({ ...prev, schedule: newSchedule }));
  };



  // ---- Term handlers ----
  const addTermRow = () => {
    setNewSubject(prev => ({
      ...prev,
      termData: [...prev.termData, { term: '', termStart: '', termEnd: '' }]
    }));
  };

  const removeTermRow = (index) => {
    if (newSubject.termData.length <= 1) {
      alert('You need at least one term.');
      return;
    }
    const newTermData = newSubject.termData.filter((_, i) => i !== index);
    setNewSubject(prev => ({ ...prev, termData: newTermData }));
  };

  const updateTerm = (index, field, value) => {
    const newTermData = [...newSubject.termData];
    newTermData[index][field] = value;
    setNewSubject(prev => ({ ...prev, termData: newTermData }));
  };

  return (
    <div className="bg-white rounded shadow p-4">
      {/* Student Header */}
      <div className="flex justify-between items-start mb-4">
        <div>
          <h2 className="text-2xl font-bold">
            {selectedStudent.name} {selectedStudent.surname}
          </h2>
          <p className="text-gray-600">{selectedStudent.email}</p>
          {selectedStudent.grade && (
            <p className="text-gray-600">Grade: {selectedStudent.grade}</p>
          )}
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowAddSubject(!showAddSubject)}
            className="bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700 text-sm"
          >
            + Add Subject
          </button>
          <button
            onClick={() => setShowEditStudentModal(true)}
            className="bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 text-sm"
          >
            Edit Student
          </button>
          {/*<button
            onClick={() => setShowImportModal(true)}
            className="bg-purple-600 text-white px-3 py-1 rounded hover:bg-purple-700 text-sm"
          >
            Import
          </button>*/}
        </div>
      </div>

      {/* Add Subject Form with dropdowns and searchable subject */}
      {showAddSubject && (
        <div className="mb-4 p-4 border rounded bg-gray-50">
          <h4 className="font-semibold mb-2">Add New Subject</h4>
          <form onSubmit={handleAddSubject} className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              {/* Subject – searchable input with datalist */}
              <div>
                <label className="block text-sm font-medium">Subject *</label>
                <input
                  type="text"
                  value={newSubject.subject}
                  onChange={e => handleNewSubjectChange('subject', e.target.value)}
                  list="subject-options"
                  className="w-full border rounded px-2 py-1"
                  placeholder="Type or select a subject"
                  required
                />
                <datalist id="subject-options">
                  {SUBJECTS.map(subj => (
                    <option key={subj} value={subj} />
                  ))}
                </datalist>
              </div>

              {/* Grade Level + Stream */}
              <div className="grid grid-cols-1 gap-2">
                <div>
                  <label className="block text-sm font-medium">Grade Level *</label>
                  <select
                    value={newSubject.gradeLevel}
                    onChange={e => handleNewSubjectChange('gradeLevel', e.target.value)}
                    className="w-full border rounded px-2 py-1"
                    required
                  >
                    <option value="">Select Grade</option>
                    {GRADE_LEVELS.map(level => (
                      <option key={level} value={level}>{level}</option>
                    ))}
                  </select>
                </div>
                {/*<div>
                  <label className="block text-sm font-medium">Stream</label>
                  <select
                    value={newSubject.stream}
                    onChange={e => handleNewSubjectChange('stream', e.target.value)}
                    className="w-full border rounded px-2 py-1"
                  >
                    <option value="">None</option>
                    {CLASS_STREAMS.map(stream => (
                      <option key={stream} value={stream}>{stream}</option>
                    ))}
                  </select>
                </div> */}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium">Google Classroom Link</label>
              <input
                type="text"
                value={newSubject.googleClassroomLink}
                onChange={e => handleNewSubjectChange('googleClassroomLink', e.target.value)}
                className="w-full border rounded px-2 py-1"
              />
            </div>
            <div>
              <label className="block text-sm font-medium">Zoom Link</label>
              <input
                type="text"
                value={newSubject.zoomLink}
                onChange={e => handleNewSubjectChange('zoomLink', e.target.value)}
                className="w-full border rounded px-2 py-1"
              />
            </div>
            <div>
              <label className="block text-sm font-medium">Zoom Password</label>
              <input
                type="text"
                value={newSubject.zoomPassword}
                onChange={e => handleNewSubjectChange('zoomPassword', e.target.value)}
                className="w-full border rounded px-2 py-1"
              />
            </div>
            {/* ===== TERM DATA SECTION ===== */}
            <div className="border-t pt-3">
              <div className="flex justify-between items-center mb-2">
                <h4 className="font-medium">Term Dates</h4>
                <button
                  type="button"
                  onClick={addTermRow}
                  className="text-sm bg-purple-600 text-white px-2 py-1 rounded hover:bg-purple-700"
                >
                  + Add Term
                </button>
              </div>
              {newSubject.termData.map((term, idx) => (
                <div key={idx} className="grid grid-cols-3 gap-2 mb-2 items-end">
                  <div>
                    <label className="block text-xs font-medium">Term Name</label>
                    <input
                      type="text"
                      value={term.term || ''}
                      onChange={e => updateTerm(idx, 'term', e.target.value)}
                      placeholder="e.g., Term 1"
                      className="w-full border rounded px-2 py-1 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium">Start Date</label>
                    <input
                      type="date"
                      value={term.termStart || ''}
                      onChange={e => updateTerm(idx, 'termStart', e.target.value)}
                      className="w-full border rounded px-2 py-1 text-sm"
                    />
                  </div>
                  <div className="flex items-end gap-1">
                    <div className="flex-1">
                      <label className="block text-xs font-medium">End Date</label>
                      <input
                        type="date"
                        value={term.termEnd || ''}
                        onChange={e => updateTerm(idx, 'termEnd', e.target.value)}
                        className="w-full border rounded px-2 py-1 text-sm"
                      />
                    </div>
                    {newSubject.termData.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeTermRow(idx)}
                        className="text-red-600 hover:text-red-800 text-sm px-1 py-1"
                        title="Remove term"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                </div>
              ))}
              {newSubject.termData.length === 0 && (
                <p className="text-sm text-gray-500">No terms added. Click "+ Add Term" to add one.</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium">Schedule</label>
              <div className="space-y-2">
                {newSubject.schedule.map((session, idx) => (
                  <div key={idx} className="grid grid-cols-3 gap-2 items-end">
                    <div>
                      <label className="block text-xs">Day</label>
                      <input
                        type="text"
                        value={session.day || ''}
                        onChange={e => updateSession(idx, 'day', e.target.value)}
                        placeholder="e.g., Monday"
                        className="w-full border rounded px-2 py-1 text-sm"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs">Start Time</label>
                      <input
                        type="text"
                        value={session.startTime || ''}
                        onChange={e => updateSession(idx, 'startTime', e.target.value)}
                        placeholder="e.g., 09:00"
                        className="w-full border rounded px-2 py-1 text-sm"
                        required
                      />
                    </div>
                    <div className="flex items-end gap-1">
                      <div className="flex-1">
                        <label className="block text-xs">Duration (min)</label>
                        <input
                          type="number"
                          value={session.duration || ''}
                          onChange={e => updateSession(idx, 'duration', e.target.value)}
                          placeholder="60"
                          className="w-full border rounded px-2 py-1 text-sm"
                          required
                        />
                      </div>
                      {newSubject.schedule.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeSession(idx)}
                          className="text-red-600 text-sm"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addSession}
                  className="text-sm bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700"
                >
                  + Add Session
                </button>
              </div>
            </div>

            <div className="flex gap-2">
              <button
                type="submit"
                disabled={addingSubject}
                className="bg-green-600 text-white px-4 py-1 rounded hover:bg-green-700 disabled:opacity-50"
              >
                {addingSubject ? 'Adding...' : 'Add Subject'}
              </button>
              <button
                type="button"
                onClick={() => setShowAddSubject(false)}
                className="bg-gray-300 px-4 py-1 rounded hover:bg-gray-400"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Subjects Tabs */}
      {subjects.length > 1 && (
        <div className="border-b border-gray-200 mb-4">
          <nav className="flex space-x-4">
            {subjects.map((subj) => (
              <button
                key={subj.userId + subj.subject}
                onClick={() => setActiveSubject(subj)}
                className={`py-2 px-3 capitalize ${currentSubject === subj
                  ? 'border-b-2 border-blue-600 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
                  }`}
              >
                {subj.subject} ({subj.grade})
              </button>
            ))}
          </nav>
        </div>
      )}

      {/* Subject Details */}
      {currentSubject && (
        <div>
          <div className="flex justify-between items-center mb-2">
            <h3 className="text-lg font-semibold">
              {currentSubject.subject} – {currentSubject.grade}
            </h3>
            <button
              onClick={() => handleEditSchedule(currentSubject)}
              className="bg-yellow-600 text-white px-3 py-1 rounded hover:bg-yellow-700 text-sm"
            >
              Edit Schedule
            </button>
          </div>

          <div className="mb-4">
            <h4 className="font-medium">Schedule</h4>
            {currentSubject.schedule && currentSubject.schedule.length > 0 ? (
              <ul className="list-disc list-inside text-sm">
                {currentSubject.schedule.map((s, idx) => (
                  <li key={idx}>{s.day} at {s.startTime} ({s.duration} mins)</li>
                ))}
              </ul>
            ) : (
              <p className="text-gray-500 text-sm">No schedule set.</p>
            )}
          </div>

          <div className="mb-4 flex flex-wrap gap-4">
            {currentSubject.googleClassroomLink && (
              <a
                href={currentSubject.googleClassroomLink}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline text-sm"
              >
                Google Classroom
              </a>
            )}
            {currentSubject.zoomLink && (
              <a
                href={currentSubject.zoomLink}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline text-sm"
              >
                Zoom Link
              </a>
            )}
          </div>


          {/* Tabs: attendance, marks, topics */}
          <div className="border-b border-gray-200 mb-4">
            <nav className="flex space-x-4">
              {['attendance', 'marks', 'topics'].map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`py-2 px-3 capitalize ${activeTab === tab
                    ? 'border-b-2 border-blue-600 text-blue-600'
                    : 'text-gray-500 hover:text-gray-700'
                    }`}
                >
                  {tab}
                </button>
              ))}
            </nav>
          </div>

          {/*activeTab === 'attendance' && (
            <AttendanceRegister
              classItem={{
                email: user.email,
                subject: currentSubject.subject,
                grade: currentSubject.grade,
                schedule: currentSubject.schedule,
                zoomLink: currentSubject.zoomLink || '',
                zoomPassword: currentSubject.zoomPassword || '',
              }}
              students={[currentSubject]}
              spreadsheetId={attendanceSheetId}
            />
          )*/}

          {activeTab === 'attendance' && (
            <AttendanceRegister
              key={`${currentSubject.subject}-${currentSubject.grade}`} // 👈 ADD THIS
              classItem={{
                email: user.email,
                subject: currentSubject.subject,
                grade: currentSubject.grade,
                schedule: currentSubject.schedule,
                zoomLink: currentSubject.zoomLink || '',
                zoomPassword: currentSubject.zoomPassword || '',
              }}
              students={[currentSubject]}
              spreadsheetId={attendanceSheetId}
            />
          )}

          {activeTab === 'marks' && (
            <MarksEntry
              classItem={{
                email: user.email,
                subject: currentSubject.subject,
                grade: currentSubject.grade,
              }}
              students={[currentSubject]}
              spreadsheetId={marksSheetId}
            />
          )}

          {activeTab === 'topics' && (
            <TopicsManager
              classItem={{
                email: user.email,
                subject: currentSubject.subject,
                grade: currentSubject.grade,
              }}
              spreadsheetId={topicsSheetId}
            />
          )}
        </div>
      )}

      {/* Modals */}
      {editSubject && (
        <EditStudentScheduleModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          student={editSubject}
          onSave={handleSaveSchedule}
        />
      )}
      <EditTutoringStudentModal
        isOpen={showEditStudentModal}
        onClose={() => setShowEditStudentModal(false)}
        student={selectedStudent}
        onSave={onEditStudent}
      />
      <TutorImportModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onImportComplete={onStudentUpdated}
      />
    </div>
  );
};

export default TutorContent;